import { useFlags, useFlagsmith, useFlagsmithLoading } from "flagsmith/react";
import {
  ListOffer,
  ReviewOffer,
} from "../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { FeatureFlags } from "fixtures/features";
import { getOfferReviewSimplifiedFlagInputs } from "utils/getOfferReviewSimplifiedFlagInputs";
import { evaluateOfferReviewSimplifiedExperience } from "utils/shouldShowSimplifiedOfferReviewExperience";
import { isSimplifiedPresentationFlagsReady } from "utils/isSimplifiedPresentationFlagsReady";
import type {
  SimplifiedExperienceDecision,
  SimplifiedExperienceIneligibleReason,
} from "utils/simplifiedExperienceDecision";

type UseOfferReviewSimplifiedIneligibleReasonResult = {
  reason: SimplifiedExperienceIneligibleReason | null;
  breezeCreatedDateCutoff: string | undefined;
};

type OfferReviewPresentationArgs = {
  presentationSource?: "offerReview";
  offer: ReviewOffer | undefined;
  offers: ListOffer[] | undefined;
  isCustomer: boolean;
};

type CapPresentationArgs = {
  presentationSource: "cap";
  isCustomer: boolean;
  isCapSimplifiedExperience: boolean;
  capSimplifiedIneligibleReason: SimplifiedExperienceIneligibleReason | null;
  isCapSimplifiedGatePending: boolean;
};

type UseOfferReviewSimplifiedIneligibleReasonArgs =
  | OfferReviewPresentationArgs
  | CapPresentationArgs;

const TRANSIENT_INELIGIBLE_REASONS =
  new Set<SimplifiedExperienceIneligibleReason>([
    "FLAGS_NOT_INITIALISED",
    "FLAGS_LOADING",
    "CAP_PAGE_LOADING",
  ]);

const resolveClassicViewIneligibleReasonForRep = ({
  isCustomer,
  offerReviewSimplifiedEnabled,
  flagsReady,
  decision,
  createdDateCutoff,
}: {
  isCustomer: boolean;
  offerReviewSimplifiedEnabled: boolean;
  flagsReady: boolean;
  decision: SimplifiedExperienceDecision | null;
  createdDateCutoff: string | undefined;
}): UseOfferReviewSimplifiedIneligibleReasonResult => {
  if (isCustomer || !flagsReady || !offerReviewSimplifiedEnabled) {
    return { reason: null, breezeCreatedDateCutoff: undefined };
  }

  if (!decision) {
    return { reason: null, breezeCreatedDateCutoff: createdDateCutoff };
  }

  if (decision.show) {
    return { reason: null, breezeCreatedDateCutoff: createdDateCutoff };
  }

  const reason = decision.reason;
  if (!reason || TRANSIENT_INELIGIBLE_REASONS.has(reason)) {
    return { reason: null, breezeCreatedDateCutoff: createdDateCutoff };
  }

  return { reason, breezeCreatedDateCutoff: createdDateCutoff };
};

const useOfferReviewSimplifiedIneligibleReason = (
  args: UseOfferReviewSimplifiedIneligibleReasonArgs
): UseOfferReviewSimplifiedIneligibleReasonResult => {
  const flagsmith = useFlagsmith();
  const flagsmithLoading = useFlagsmithLoading();
  const flags = useFlags([FeatureFlags.OFFER_REVIEW_SIMPLIFIED]);

  const flagsReady = isSimplifiedPresentationFlagsReady(
    flagsmith,
    flagsmithLoading
  );

  const {
    offerReviewSimplifiedEnabled,
    presentationTargets,
    createdDateCutoff,
  } = flagsReady
    ? getOfferReviewSimplifiedFlagInputs(flagsmith, flags)
    : {
        offerReviewSimplifiedEnabled: false,
        presentationTargets: [],
        createdDateCutoff: undefined,
      };

  if (args.presentationSource === "cap") {
    const decision: SimplifiedExperienceDecision | null =
      args.isCapSimplifiedGatePending
        ? null
        : {
            show: args.isCapSimplifiedExperience,
            reason: args.capSimplifiedIneligibleReason,
          };

    return resolveClassicViewIneligibleReasonForRep({
      isCustomer: args.isCustomer,
      offerReviewSimplifiedEnabled,
      flagsReady,
      decision,
      createdDateCutoff,
    });
  }

  const { offer, offers, isCustomer } = args;

  if (!offer) {
    return resolveClassicViewIneligibleReasonForRep({
      isCustomer,
      offerReviewSimplifiedEnabled,
      flagsReady,
      decision: null,
      createdDateCutoff,
    });
  }

  const offerCreatedAt = offers?.find(
    (entry) => entry.id === offer.id
  )?.createdAt;
  const summary = offer.financial?.summary;

  const decision = evaluateOfferReviewSimplifiedExperience({
    offerReviewSimplifiedEnabled: true,
    presentationTargets,
    createdDateCutoff,
    offerCreatedAt,
    state: summary?.state,
    utility: summary?.utility,
    offer,
  });

  return resolveClassicViewIneligibleReasonForRep({
    isCustomer,
    offerReviewSimplifiedEnabled,
    flagsReady,
    decision,
    createdDateCutoff,
  });
};

export { useOfferReviewSimplifiedIneligibleReason };
export type {
  UseOfferReviewSimplifiedIneligibleReasonArgs,
  UseOfferReviewSimplifiedIneligibleReasonResult,
};
